import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, PenSquare } from "lucide-react";
import { JobDescriptionUploadDropzone } from "./JobDescriptionUploadDropzone";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface JobCreationMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMethod: (method: 'upload' | 'paste' | 'manual', file?: File) => void;
}

export const JobCreationMethodModal = ({
  isOpen,
  onClose,
  onSelectMethod
}: JobCreationMethodModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const { t } = useTranslation();

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    onSelectMethod('upload', selectedFile);
  };

  const handleUploadClick = () => {
    onSelectMethod('upload');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh]">
        <DialogHeader className="text-center pb-4">
          <DialogTitle className="text-2xl font-semibold">{t("jobs.jobOfferModal.chooseMethod")}</DialogTitle>
        </DialogHeader>
        
        <div className="px-4 py-6">
          <div className="grid grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Upload Document Option */}
            <div
              onClick={handleUploadClick}
              className="cursor-pointer border rounded-sm hover:border-blue-500 transition-colors overflow-hidden h-[280px]"
            >
              <JobDescriptionUploadDropzone 
                onFileSelect={(file) => {
                  handleFileSelect(file);
                }}
                isFileUploadEnabled={false} // Disable file upload in step 1
              />
            </div>
            
            {/* Copy & Paste Option */}
            <div 
              onClick={() => onSelectMethod('paste')}
              className="cursor-pointer border rounded-sm hover:border-blue-500 p-8 flex flex-col items-center text-center h-[280px]"
            >
              <div className="w-24 h-24 flex items-center justify-center text-blue-800 mb-6">
                <FileText size={56} strokeWidth={1.25} />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t("jobs.jobOfferModal.pasteOption")}</h3>
              <p className="text-gray-500 text-sm">
                {t("jobs.jobOfferModal.pasteOptionDesc")}
              </p>
            </div>
            
            {/* Manual Entry Option */}
            <div 
              onClick={() => onSelectMethod('manual')}
              className="cursor-pointer border rounded-sm hover:border-blue-500 p-8 flex flex-col items-center text-center h-[280px]"
            >
              <div className="w-24 h-24 flex items-center justify-center text-blue-800 mb-6">
                <PenSquare size={56} strokeWidth={1.25} />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t("jobs.jobOfferModal.manualOption")}</h3>
              <p className="text-gray-500 text-sm">
                {t("jobs.jobOfferModal.manualOptionDesc")}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
